import React, { useEffect, useState } from "react";
import GenericList from "../shared/components/GenericList";
import { GridColDef } from "@mui/x-data-grid";
import { questionnaireUrl } from "../../configs/URL";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  LineChart,
  Line,
} from "recharts";
import { Grid2 } from "@mui/material";

export interface Questionnaire {
  id: number;
  Nom: string;
  ZoneGeo: string;
  typeMateriel: string;
  FrequenceCollecte: string;
  QuantiteDechet: number;
  DureeMoyenne: number;
  Probleme: string;
  SecuriteEtCondition: string;
  EtatEquipementUtilise: string;
  NbPersonneMenage: number;
  FrequenceServiceCollecte: string;
  Ponctualite: string;
  ProprieteRue: string;
  ModeStockage: string;
  PratiqueDeTri: string;
  ContributionFinanciere: string;
  Suggestion: string;
  LocalistionRegroupement: string;
  TypePointCollecyr: string;
  FrequenceCollecte_001: string;
  VolumeMoyenneDechet: number;
  PropreteDuSite: string;
  ProblemeSurSite: string;
  Responsable: string;
  DechetNonCollecte: string;
  IncidentSanitaire: string;
  ImpactEnvironnement: string;
  BrulageDechet: string;
  NettoyageRegroupement: string;
  CoutMensuel: number;
  CoutMoyenParTonne: number;
  QuantiteCarburant: number;
  CoutMateriel: number;
  RecetteCollecte: number;
  TauxCouverture: number;
  TempsMoyenCollecte: number;
  NombrePlainte: number;
  TauxSatisfaction: number;
  DefisMajeur: string;
}

export const QuestionnaireTable: React.FC = () => {
  const [data, setData] = useState<Questionnaire[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const res = await fetch(questionnaireUrl);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const json = await res.json();

        const mapped: Questionnaire[] = json.results.map((r: any) => {
          const result: Questionnaire = {
            id: r._id,
            Nom: r.Nom,
            ZoneGeo: r.ZoneGeo,
            typeMateriel: r.typeMateriel,
            FrequenceCollecte: r.FrequenceCollecte,
            QuantiteDechet: Number(r.QuantiteDechet) || 0,
            DureeMoyenne: Number(r.DureeMoyenne) || 0,
            Probleme: r.Probleme,
            SecuriteEtCondition: r.SecuriteEtCondition,
            EtatEquipementUtilise: r.EtatEquipementUtilise,
            NbPersonneMenage: Number(r.NbPersonneMenage) || 0,
            FrequenceServiceCollecte: r.FrequenceServiceCollecte,
            Ponctualite: r.Ponctualite,
            ProprieteRue: r.ProprieteRue,
            ModeStockage: r.ModeStockage,
            PratiqueDeTri: r.PratiqueDeTri,
            ContributionFinanciere: r.ContributionFinanciere,
            Suggestion: r.Suggestion,
            LocalistionRegroupement: r.LocalistionRegroupement,
            TypePointCollecyr: r.TypePointCollecyr,
            FrequenceCollecte_001: r.FrequenceCollecte_001,
            VolumeMoyenneDechet: Number(r.VolumeMoyenneDechet) || 0,
            PropreteDuSite: r.PropreteDuSite,
            ProblemeSurSite: r.ProblemeSurSite,
            Responsable: r.Responsable,
            DechetNonCollecte: r.DechetNonCollecte,
            IncidentSanitaire: r.IncidentSanitaire,
            ImpactEnvironnement: r.ImpactEnvironnement,
            BrulageDechet: r.BrulageDechet,
            NettoyageRegroupement: r.NettoyageRegroupement,
            CoutMensuel: Number(r.CoutMensuel) || 0,
            CoutMoyenParTonne: Number(r.CoutMoyenParTonne) || 0,
            QuantiteCarburant: Number(r.QuantiteCarburant) || 0,
            CoutMateriel: Number(r.CoutMateriel) || 0,
            RecetteCollecte: Number(r.RecetteCollecte) || 0,
            TauxCouverture: Number(r.TauxCouverture) || 0,
            TempsMoyenCollecte: Number(r.TempsMoyenCollecte) || 0,
            NombrePlainte: Number(r.NombrePlainte) || 0,
            TauxSatisfaction: Number(r.TauxSatisfaction) || 0,
            DefisMajeur: r.DefisMajeur,
          };
          return result;
        });

        setData(mapped);
      } catch (err: any) {
        setError(err.message || "Erreur de chargement");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  if (loading) return <div>Chargement…</div>;
  if (error) return <div style={{ color: "red" }}>Erreur: {error}</div>;

  const QuestionnaireField: GridColDef[] = Object.keys(data[0] || {}).map((key) => ({
    field: key,
    headerName: key,
    width: 200,
  }));

  return (
    <div style={{ padding: "16px" }}>
      <Grid2 container spacing={2} style={{ marginBottom: "16px" }}>
        <Grid2 size={4}>
          <h3 style={{ textAlign: "center", marginBottom: 10 }}>
            Quantité de déchet vs Durée moyenne
          </h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={data}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="Nom" />
              <YAxis />
              <Tooltip />
              <Legend />
              <Bar dataKey="QuantiteDechet" name="Quantité de déchet" fill="#8884d8" />
              <Bar dataKey="DureeMoyenne" name="Durée moyenne" fill="#82ca9d" />
            </BarChart>
          </ResponsiveContainer>
        </Grid2>

        <Grid2 size={4}>
          <h3 style={{ textAlign: "center", marginBottom: 10 }}>
            Taux de satisfaction vs Taux de couverture
          </h3>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={data}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="Nom" />
              <YAxis />
              <Tooltip />
              <Legend />
              <Line
                type="monotone"
                dataKey="TauxSatisfaction"
                stroke="#8884d8"
                name="Taux de satisfaction"
              />
              <Line
                type="monotone"
                dataKey="TauxCouverture"
                stroke="#82ca9d"
                name="Taux de couverture"
              />
            </LineChart>
          </ResponsiveContainer>
        </Grid2>

        <Grid2 size={4}>
          <h3 style={{ textAlign: "center", marginBottom: 10 }}>
            Coût mensuel vs Recette
          </h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={data}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="Nom" />
              <YAxis />
              <Tooltip />
              <Legend />
              <Bar dataKey="CoutMensuel" name="Coût mensuel" fill="#ff7300" />
              <Bar dataKey="RecetteCollecte" name="Recette collecte" fill="#387908" />
            </BarChart>
          </ResponsiveContainer>
        </Grid2>
      </Grid2>

      <GenericList columns={QuestionnaireField} rows={data} />
    </div>
  );
};

export default QuestionnaireTable;
